import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  VizPortalDQWDataCatalogProviderProps,
  WarningStatusChip,
  WarningStatusProps
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import * as React from 'react';
import { Query } from 'react-apollo';

import {
  VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery,
  VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery';
import { VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryGql } from '../../queries/VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryGql';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { renderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';

interface LineageWarningVirtualConnectionTableProps {
  readonly asset: AssetMarkedByIndicator;
  readonly vizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddableVirtualConnectionTableDataQualityWarningProps = VizPortalDQWDataCatalogProviderProps & LineageWarningVirtualConnectionTableProps;

export function EmbeddableVirtualConnectionTableDataQualityWarning(props: EmbeddableVirtualConnectionTableDataQualityWarningProps): JSX.Element {
  const { featureFlags: { publishedConnectionsLineageEnabled }, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery, VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryVariables>
          query={VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryGql}
          variables={{ vizportalUrlId: props.vizportalUrlId, first: 10, offset: 0, publishedConnectionsLineageEnabled }}
        >
          {result => renderQueryResult(result, {
            renderLoading: () => renderWarnings(urls, { ...props }),
            renderData: data => renderWarnings(urls, { ...props }, data),
          })}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddableVirtualConnectionTableDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  data?: VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery
): JSX.Element | null {
  const virtualConnectionTable = data && data.virtualConnectionTables ? data.virtualConnectionTables[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive)
  if (!virtualConnectionTable && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = virtualConnectionTable
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(virtualConnectionTable.id, virtualConnectionTable.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings

  const props: WarningStatusProps = {
    subjectArea: 'VirtualConnectionTable',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
  }

  return <WarningStatusChip {...props} />
}
